import { useEffect, useRef, useState } from "react";
import api, { fmtQty, fmtMoney } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { PageHeader, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { ScanLine, Search, Square } from "lucide-react";

export default function Scan() {
  const { t } = useI18n();
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [item, setItem] = useState(null);
  const [balances, setBalances] = useState([]);
  const scannerRef = useRef(null);

  useEffect(() => () => { stopScan(); }, []); // eslint-disable-line

  const lookup = async (code) => {
    const num = code.trim().toUpperCase();
    if (!num) return;
    const { data } = await api.get("/items", { params: { q: num } });
    const found = data.find((i) => i.item_number.toUpperCase() === num) || data[0];
    if (!found) {
      toast.error(`${t("scan.notFound")}: ${num}`);
      setItem(null);
      setBalances([]);
      return;
    }
    setItem(found);
    const bals = await api.get("/balances");
    setBalances(bals.data.filter((b) => b.item_number === found.item_number));
  };

  const startScan = async () => {
    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      setScanning(true);
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 220 },
        (decoded) => { lookup(decoded); stopScan(); },
        () => {}
      );
    } catch (e) {
      toast.error(t("scan.camError"));
      setScanning(false);
    }
  };

  const stopScan = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch (e) {}
    setScanning(false);
  };

  return (
    <div data-testid="scan-page" className="max-w-md mx-auto lg:mx-0">
      <PageHeader title={t("scan.title")} subtitle={t("scan.subtitle")} />

      <div className="border bg-card p-4 mb-4">
        <div id="qr-reader" className={scanning ? "mb-3" : "hidden"} />
        {!scanning ? (
          <Button onClick={startScan} className="rounded-none font-bold w-full h-12 text-sm" data-testid="scan-start-button">
            <ScanLine size={18} className="mr-2" /> {t("scan.start")}
          </Button>
        ) : (
          <Button onClick={stopScan} variant="destructive" className="rounded-none font-bold w-full" data-testid="scan-stop-button">
            <Square size={14} className="mr-2" /> {t("scan.stop")}
          </Button>
        )}
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-4 mb-2">{t("scan.manual")}</div>
        <form onSubmit={(e) => { e.preventDefault(); lookup(manual); }} className="flex gap-2">
          <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="ITM-100"
            className="rounded-none h-10 text-sm" data-testid="scan-manual-input" />
          <Button type="submit" className="rounded-none font-bold h-10" data-testid="scan-lookup-button">
            <Search size={15} />
          </Button>
        </form>
      </div>

      {item && (
        <div className="border bg-card p-4" data-testid="scan-result">
          <div className="text-xl font-black tracking-tight">{item.item_number}</div>
          <div className="text-sm text-muted-foreground mb-1">{item.description}</div>
          <div className="text-xs mb-3">
            {item.category} · {t("it.uomP")}: {item.uom_primary}
            {item.uom_secondary && ` · ${item.uom_secondary} x${fmtQty(item.conversion_factor)}`}
            {item.lot_controlled && <span className="text-primary font-bold"> · LOT</span>}
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr>
                <th>{t("common.warehouse")}</th><th>{t("loc.bin")}</th><th>{t("common.status")}</th>
                <th className="num">{t("cx.onHand")}</th><th className="num">{t("cx.avgCost")}</th>
              </tr></thead>
              <tbody>
                {balances.map((b) => (
                  <tr key={b.id}>
                    <td className="font-bold">{b.warehouse}</td>
                    <td>{b.location || "—"}</td>
                    <td><StatusBadge status={b.stock_status} /></td>
                    <td className="num font-bold">{fmtQty(b.qty_on_hand)}</td>
                    <td className="num">{fmtMoney(b.avg_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
